import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  Settings,
  DollarSign,
  Car,
  Save,
  Loader2,
  Info,
  Building2,
  MapPin,
  Phone,
  Mail,
  Globe
} from 'lucide-react';

const SettingsPage = () => {
  const { token } = useAuth();
  const [mileageRate, setMileageRate] = useState('');
  const [mileageYear, setMileageYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  
  // Company settings
  const [company, setCompany] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    phone: '',
    email: '',
    website: ''
  });

  const fetchSettings = async () => {
    try {
      const [mileageRes, companyRes] = await Promise.all([
        axios.get(`${API}/settings/mileage-rate`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/settings/company`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setMileageRate(mileageRes.data.rate.toString());
      setMileageYear(mileageRes.data.year);
      setCompany(companyRes.data);
    } catch (err) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const handleSaveMileageRate = async () => {
    if (!mileageRate || parseFloat(mileageRate) <= 0) {
      toast.error('Please enter a valid mileage rate');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings/mileage-rate`, {
        irs_mileage_rate: parseFloat(mileageRate),
        year: mileageYear
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Mileage rate updated');
    } catch (err) {
      toast.error('Failed to update mileage rate');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompany = async () => {
    if (!company.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSavingCompany(true);
    try {
      await axios.put(`${API}/settings/company`, company, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company information updated');
    } catch (err) {
      toast.error('Failed to update company information');
    } finally {
      setSavingCompany(false);
    }
  };

  const updateCompanyField = (field, value) => {
    setCompany(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3C4F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage system configuration</p>
      </div>

      <div className="grid gap-6 max-w-2xl">
        {/* Company Information */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#1F3C4F]/10 rounded-lg">
                <Building2 className="w-5 h-5 text-[#1F3C4F]" />
              </div>
              <div>
                <CardTitle className="font-heading">Company Information</CardTitle>
                <CardDescription>Your business details for invoices and documents</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Company Name */}
            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={company.name}
                onChange={(e) => updateCompanyField('name', e.target.value)}
                placeholder="Your Company Name"
                data-testid="company-name-input"
              />
            </div>

            {/* Address */}
            <div className="space-y-2">
              <Label htmlFor="address1">Address Line 1</Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="address1"
                  className="pl-9"
                  value={company.address_line1}
                  onChange={(e) => updateCompanyField('address_line1', e.target.value)}
                  placeholder="Street address"
                  data-testid="company-address1-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address2">Address Line 2</Label>
              <Input
                id="address2"
                value={company.address_line2}
                onChange={(e) => updateCompanyField('address_line2', e.target.value)}
                placeholder="Suite, unit, building, floor, etc."
                data-testid="company-address2-input"
              />
            </div>

            {/* City, State, Zip */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={company.city}
                  onChange={(e) => updateCompanyField('city', e.target.value)}
                  placeholder="City"
                  data-testid="company-city-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">State</Label>
                <Input
                  id="state"
                  value={company.state}
                  onChange={(e) => updateCompanyField('state', e.target.value)}
                  placeholder="State"
                  data-testid="company-state-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="zip">ZIP Code</Label>
                <Input
                  id="zip"
                  value={company.zip_code}
                  onChange={(e) => updateCompanyField('zip_code', e.target.value)}
                  placeholder="ZIP"
                  data-testid="company-zip-input"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="phone"
                    className="pl-9"
                    value={company.phone}
                    onChange={(e) => updateCompanyField('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                    data-testid="company-phone-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    className="pl-9"
                    value={company.email}
                    onChange={(e) => updateCompanyField('email', e.target.value)}
                    placeholder="info@company.com"
                    data-testid="company-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website">Website</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="website"
                  className="pl-9"
                  value={company.website}
                  onChange={(e) => updateCompanyField('website', e.target.value)}
                  placeholder="www.yourcompany.com"
                  data-testid="company-website-input"
                />
              </div>
            </div>

            <Button 
              onClick={handleSaveCompany} 
              disabled={savingCompany}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              data-testid="save-company-btn"
            >
              {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Company Info
            </Button>
          </CardContent>
        </Card>

        {/* Mileage Rate Setting */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#FFB800]/10 rounded-lg">
                <Car className="w-5 h-5 text-[#FFB800]" />
              </div>
              <div>
                <CardTitle className="font-heading">IRS Mileage Rate</CardTitle>
                <CardDescription>Standard mileage rate for expense calculations</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-700">
                <p className="font-medium">2025 IRS Standard Mileage Rate</p>
                <p className="mt-1">
                  The IRS standard mileage rate for 2025 is $0.70 per mile for business use.
                  Update this rate annually to ensure accurate expense calculations.
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mileageRate">Rate per Mile ($)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="mileageRate"
                    type="number"
                    step="0.01"
                    className="pl-9"
                    value={mileageRate}
                    onChange={(e) => setMileageRate(e.target.value)}
                    data-testid="mileage-rate-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mileageYear">Year</Label>
                <Input
                  id="mileageYear"
                  type="number"
                  value={mileageYear}
                  onChange={(e) => setMileageYear(parseInt(e.target.value))}
                  data-testid="mileage-year-input"
                />
              </div>
            </div>

            <Button 
              onClick={handleSaveMileageRate} 
              disabled={saving}
              className="bg-[#1F3C4F] hover:bg-[#152a38]"
              data-testid="save-mileage-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Expense Categories Info */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Expense Categories</CardTitle>
            <CardDescription>Available expense tracking categories</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { name: 'Mileage', desc: 'Vehicle travel' },
                { name: 'Receipts', desc: 'General expenses' },
                { name: 'Meals', desc: 'Business meals' },
                { name: 'Travel - Hotel', desc: 'Lodging' },
                { name: 'Travel - Air', desc: 'Flights' },
                { name: 'Subscriptions', desc: 'Software/services' },
                { name: 'Materials', desc: 'Supplies' },
                { name: 'Contractor', desc: 'Freelance work' },
                { name: 'Other', desc: 'Miscellaneous' }
              ].map((cat) => (
                <div key={cat.name} className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium text-sm">{cat.name}</p>
                  <p className="text-xs text-slate-500">{cat.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SettingsPage;
